import { useState, useEffect, useCallback } from 'react';
import {
  Box, Title, Text, TextInput, Switch, Loader, Alert,
  Anchor, Group, Pagination,
} from '@mantine/core';
import { IconSearch, IconAlertCircle, IconDownload } from '@tabler/icons-react';
import { useAuth } from '../../shared/contexts/AuthContext';
import UseCaseCard from './components/UseCaseCard';
import { ivrSearch, ivrExportCsv } from './api_scripts/ivrClient';

const PAGE_SIZE = 40;

const VERTICALS = ['Energy/Utilities', 'Healthcare', 'Financial Services', 'Government', 'Retail', 'Fintech', 'Other'];
const PLATFORMS = ['UCCE/PCCE', 'UCCE', 'Webex CC', 'UCCX', 'Amazon Connect', 'Twilio', 'Other'];
const COMPLEXITY = [
  { value: 'S',  color: '#2f9e44', bg: '#ebfbee', border: '#b2f2bb' },
  { value: 'M',  color: '#1971c2', bg: '#e7f5ff', border: '#a5d8ff' },
  { value: 'L',  color: '#e67700', bg: '#fff9db', border: '#ffec99' },
  { value: 'XL', color: '#c92a2a', bg: '#fff5f5', border: '#ffc9c9' },
];

const LABEL_STYLE = { fontSize: 11, fontWeight: 600, color: '#868e96', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 };

const EMPTY_FILTERS = { query: '', vertical: '', platform: '', complexities: [], yearMin: '', yearMax: '', selfServiceOnly: false };

export default function IvrSearch() {
  const { getApiToken } = useAuth();

  // Input state (user is typing)
  const [query, setQuery] = useState('');
  const [vertical, setVertical] = useState('');
  const [platform, setPlatform] = useState('');
  const [complexities, setComplexities] = useState([]);
  const [yearMin, setYearMin] = useState('');
  const [yearMax, setYearMax] = useState('');
  const [selfServiceOnly, setSelfServiceOnly] = useState(false);

  // Applied state (what was last sent to the API)
  const [applied, setApplied] = useState(EMPTY_FILTERS);

  // Pagination
  const [page, setPage] = useState(1);

  // Results
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasFilters = vertical || platform || complexities.length > 0 || yearMin || yearMax || selfServiceOnly;

  // Debounce: any input change resets page to 1 and commits to applied state
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setApplied({ query, vertical, platform, complexities, yearMin, yearMax, selfServiceOnly });
    }, 400);
    return () => clearTimeout(timer);
  }, [query, vertical, platform, complexities, yearMin, yearMax, selfServiceOnly]);

  // Fetch whenever page or applied filters change
  const doSearch = useCallback(async (pageNum, filters) => {
    setLoading(true);
    setError(null);
    try {
      const data = await ivrSearch({
        query: filters.query || '',
        filters: {
          vertical: filters.vertical || null,
          platform: filters.platform || null,
          complexity: filters.complexities.length ? filters.complexities : null,
          self_service_only: filters.selfServiceOnly || null,
          year_min: filters.yearMin ? parseInt(filters.yearMin, 10) : null,
          year_max: filters.yearMax ? parseInt(filters.yearMax, 10) : null,
        },
        page: pageNum,
        pageSize: PAGE_SIZE,
      }, getApiToken);
      setResults(data.results || []);
      setTotal(data.total ?? 0);
      setPages(data.pages ?? 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getApiToken]);

  useEffect(() => {
    doSearch(page, applied);
  }, [page, applied, doSearch]);

  const toggleVertical = (v) => setVertical(prev => prev === v ? '' : v);
  const togglePlatform = (p) => setPlatform(prev => prev === p ? '' : p);
  const toggleComplexity = (c) => setComplexities(prev =>
    prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
  );

  const clearAll = () => {
    setQuery('');
    setVertical('');
    setPlatform('');
    setComplexities([]);
    setYearMin('');
    setYearMax('');
    setSelfServiceOnly(false);
  };

  // Export ALL matching use cases via backend endpoint (not just current page)
  const exportCsv = async () => {
    try {
      const blob = await ivrExportCsv({
        filters: {
          vertical: applied.vertical || null,
          platform: applied.platform || null,
          complexity: applied.complexities.length ? applied.complexities : null,
          self_service_only: applied.selfServiceOnly || null,
          year_min: applied.yearMin ? parseInt(applied.yearMin, 10) : null,
          year_max: applied.yearMax ? parseInt(applied.yearMax, 10) : null,
        },
      }, getApiToken);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `use-cases-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  };

  const start = total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const end = Math.min(page * PAGE_SIZE, total);

  return (
    <Box style={{ display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <Box style={{ padding: '32px 40px 24px', borderBottom: '1px solid #e9ecef', background: '#ffffff' }}>
        <Title order={2} size="1.5rem" fw={700}>IVR Use Case Library</Title>
        <Text c="dimmed" size="sm" mt={4}>Search across all extracted self-service IVR use cases</Text>
      </Box>

      {/* Search bar */}
      <Box style={{ padding: '20px 40px', background: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
        <TextInput
          placeholder="Search use cases, customers, integrations..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          leftSection={<IconSearch size={16} />}
          size="md"
        />
      </Box>

      {/* Body */}
      <Box style={{ display: 'flex', flex: 1 }}>

        {/* Filter panel */}
        <Box style={{
          width: 240,
          minWidth: 240,
          padding: '20px 20px',
          borderRight: '1px solid #e9ecef',
          background: '#ffffff',
          overflowY: 'auto',
        }}>

          {/* Vertical */}
          <Box mb={20}>
            <div style={LABEL_STYLE}>Vertical</div>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {VERTICALS.map(v => (
                <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#495057' }}>
                  <input
                    type="checkbox"
                    checked={vertical === v}
                    onChange={() => toggleVertical(v)}
                    style={{ accentColor: '#4263eb' }}
                  />
                  {v}
                </label>
              ))}
            </Box>
          </Box>

          {/* Platform */}
          <Box mb={20}>
            <div style={LABEL_STYLE}>Platform</div>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {PLATFORMS.map(p => (
                <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#495057' }}>
                  <input
                    type="checkbox"
                    checked={platform === p}
                    onChange={() => togglePlatform(p)}
                    style={{ accentColor: '#4263eb' }}
                  />
                  {p}
                </label>
              ))}
            </Box>
          </Box>

          {/* Complexity */}
          <Box mb={20}>
            <div style={LABEL_STYLE}>Complexity</div>
            <Group gap={6}>
              {COMPLEXITY.map(({ value, color, bg, border }) => {
                const active = complexities.includes(value);
                return (
                  <button
                    key={value}
                    onClick={() => toggleComplexity(value)}
                    style={{
                      height: 28,
                      minWidth: 36,
                      borderRadius: 6,
                      border: `1px solid ${active ? border : '#dee2e6'}`,
                      background: active ? bg : '#ffffff',
                      color: active ? color : '#868e96',
                      fontWeight: active ? 700 : 400,
                      fontSize: 12,
                      cursor: 'pointer',
                      padding: '0 8px',
                    }}
                  >
                    {value}
                  </button>
                );
              })}
            </Group>
          </Box>

          {/* Year */}
          <Box mb={20}>
            <div style={LABEL_STYLE}>Year</div>
            <Group gap={8}>
              <TextInput
                placeholder="From"
                value={yearMin}
                onChange={e => setYearMin(e.target.value)}
                size="xs"
                style={{ width: 72 }}
              />
              <Text size="xs" c="dimmed">—</Text>
              <TextInput
                placeholder="To"
                value={yearMax}
                onChange={e => setYearMax(e.target.value)}
                size="xs"
                style={{ width: 72 }}
              />
            </Group>
          </Box>

          {/* Self-service */}
          <Box mb={20}>
            <Group justify="space-between" align="center">
              <Text size="sm" style={{ color: '#495057' }}>Self-service only</Text>
              <Switch
                size="sm"
                checked={selfServiceOnly}
                onChange={e => setSelfServiceOnly(e.currentTarget.checked)}
                color="indigo"
              />
            </Group>
          </Box>

          {/* Clear all */}
          {hasFilters && (
            <Anchor component="button" size="xs" c="indigo" onClick={clearAll}>
              Clear all filters
            </Anchor>
          )}
        </Box>

        {/* Results area */}
        <Box style={{ flex: 1, padding: '20px 32px 32px 24px' }}>

          {/* Results bar */}
          <Group justify="space-between" align="center" mb={16}>
            <Text size="sm" c="dimmed">
              {loading ? 'Loading…' : total > 0 ? `Showing ${start}–${end} of ${total} use cases` : ''}
            </Text>
            {total > 0 && (
              <button
                onClick={exportCsv}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', borderRadius: 6, fontSize: 12,
                  border: '1px solid #dee2e6', background: '#fff',
                  cursor: 'pointer', color: '#495057',
                }}
              >
                <IconDownload size={13} />
                Export all ({total})
              </button>
            )}
          </Group>

          {/* Error */}
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb={16}>
              {error}
            </Alert>
          )}

          {/* Loading */}
          {loading && (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
              <Loader color="indigo" />
            </Box>
          )}

          {/* 2-column grid */}
          {!loading && results.length > 0 && (
            <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {results.map((uc) => (
                <UseCaseCard key={uc.id} useCase={uc} />
              ))}
            </Box>
          )}

          {!loading && !error && results.length === 0 && (
            <Box style={{ textAlign: 'center', padding: '60px 0' }}>
              <Text c="dimmed" size="sm">No results found. Try adjusting your search or filters.</Text>
            </Box>
          )}

          {/* Pagination */}
          {!loading && pages > 1 && (
            <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
              <Pagination
                total={pages}
                value={page}
                onChange={setPage}
                color="indigo"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
